import {
  Code,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatColorFill,
  FormatColorText,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatSize,
  FormatUnderlined,
  Image,
  ImageOutlined,
  Link,
  PictureAsPdf,
  Redo,
  Undo,
  WallpaperOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "../../../helpers/methods";
import { exportPdfDirectDownload } from "../../../screens/Analysis/Components/Report/ReportService";
import strings from "../../constants/StringConstants";
import CustomButton from "../CustomButton/CustomButton";

interface HtmlEditorProps {
  content: string;
  onSave?: (content: string) => void;
  fileName?: string;
  onClose?: () => void;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({
  content,
  onSave,
  fileName,
  onClose,
}) => {
  const { t } = useTranslation();
  const [htmlContent, setHtmlContent] = useState(content);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [showSizeDialog, setShowSizeDialog] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState<{
    base64: string;
    fileName: string;
  } | null>(null);
  const [history, setHistory] = useState<string[]>([content]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [showFontColorDialog, setShowFontColorDialog] = useState(false);
  const [showFillColorDialog, setShowFillColorDialog] = useState(false);
  const [showFontSizeDialog, setShowFontSizeDialog] = useState(false);
  const [fontColor, setFontColor] = useState("#000000");
  const [fillColor, setFillColor] = useState("#ffffff");
  const [fontSize, setFontSize] = useState("14px");

  useEffect(() => {
    setHtmlContent(content);
    setHistory([content]);
    setHistoryIndex(0);
  }, [content]);

  // Initialize editor content only once
  useEffect(() => {
    if (editorRef.current && content) {
      editorRef.current.innerHTML = content;
    }
  }, []); // Empty dependency array - only run once

  const addToHistory = (newContent: string) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const undo = () => {
    if (canUndo) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const newContent = history[newIndex];
      setHtmlContent(newContent);
      if (editorRef.current) {
        editorRef.current.innerHTML = newContent;
      }
    }
  };

  const redo = () => {
    if (canRedo) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const newContent = history[newIndex];
      setHtmlContent(newContent);
      if (editorRef.current) {
        editorRef.current.innerHTML = newContent;
      }
    }
  };

  const execCommand = (command: string, value?: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
      updateContent();
    }
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  };

  const restoreSelection = (range: Range | null) => {
    if (range && editorRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const updateContent = () => {
    if (editorRef.current) {
      const savedRange = saveSelection();
      const newContent = editorRef.current.innerHTML;
      // Only update if content actually changed
      if (newContent !== htmlContent) {
        setHtmlContent(newContent);
        addToHistory(newContent);
      }
      // Restore cursor position
      setTimeout(() => {
        restoreSelection(savedRange);
      }, 0);
    }
  };

  const handleEditorFocus = () => {
    if (editorRef.current) {
      // Only set cursor if there's no current selection
      const selection = window.getSelection();
      if (selection && selection.rangeCount === 0) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false); // collapse to end
        selection.addRange(range);
      }
    }
  };

  const handleImageInsert = () => {
    if (imageUrl.trim()) {
      const imgTag = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width: 100%; height: auto;" />`;
      execCommand("insertHTML", imgTag);
      setShowImageDialog(false);
      setImageUrl("");
      setImageAlt("");
    }
  };

  const handleLinkInsert = () => {
    if (linkUrl.trim() && linkText.trim()) {
      const linkTag = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      execCommand("insertHTML", linkTag);
      setShowLinkDialog(false);
      setLinkUrl("");
      setLinkText("");
    }
  };

  const handleLocalImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setSelectedImageData({ base64: base64String, fileName: file.name });
        setShowSizeDialog(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageSizeSelect = (size: string) => {
    if (selectedImageData) {
      const width = size === "auto" ? "auto" : size;
      const imgTag = `<img src="${selectedImageData.base64}" alt="${selectedImageData.fileName}" style="max-width: ${width}; width: ${width}; height: auto; margin: 8px 0;" />`;
      execCommand("insertHTML", imgTag);
      setShowSizeDialog(false);
      setSelectedImageData(null);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(htmlContent);
    }
  };

  const handleFontColorApply = () => {
    const savedRange = saveSelection();
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand("foreColor", false, fontColor);
      updateContent();
      setTimeout(() => {
        restoreSelection(savedRange);
      }, 0);
    }
    setShowFontColorDialog(false);
  };

  const handleFillColorApply = () => {
    const savedRange = saveSelection();
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand("hiliteColor", false, fillColor);
      updateContent();
      setTimeout(() => {
        restoreSelection(savedRange);
      }, 0);
    }
    setShowFillColorDialog(false);
  };

  const handleFontSizeApply = () => {
    const savedRange = saveSelection();
    if (editorRef.current) {
      editorRef.current.focus();

      // Use a simpler approach with CSS styling
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Create a span with the font size
        const span = document.createElement("span");
        span.style.fontSize = fontSize;

        // Extract the selected content
        const contents = range.extractContents();
        span.appendChild(contents);

        // Insert the styled content
        range.insertNode(span);

        // Normalize the selection
        range.selectNodeContents(span);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      updateContent();
      setTimeout(() => {
        restoreSelection(savedRange);
      }, 0);
    }
    setShowFontSizeDialog(false);
  };

  const toolbarButtons = [
    {
      icon: <FormatBold />,
      title: "Bold",
      command: "bold",
    },
    {
      icon: <FormatItalic />,
      title: "Italic",
      command: "italic",
    },
    {
      icon: <FormatUnderlined />,
      title: "Underline",
      command: "underline",
    },
    {
      icon: <FormatAlignLeft />,
      title: "Align Left",
      command: "justifyLeft",
    },
    {
      icon: <FormatAlignCenter />,
      title: "Align Center",
      command: "justifyCenter",
    },
    {
      icon: <FormatAlignRight />,
      title: "Align Right",
      command: "justifyRight",
    },
    {
      icon: <FormatListBulleted />,
      title: "Bullet List",
      command: "insertUnorderedList",
    },
    {
      icon: <FormatListNumbered />,
      title: "Numbered List",
      command: "insertOrderedList",
    },
    {
      icon: <FormatQuote />,
      title: "Quote",
      command: "formatBlock",
      value: "blockquote",
    },
    {
      icon: <Code />,
      title: "Code",
      command: "formatBlock",
      value: "pre",
    },
  ];

  const exportToPdf = async (withBackground: boolean) => {
    setExportMenuAnchor(null);
    try {
      setIsExporting(true);
      exportPdfDirectDownload(
        htmlContent,
        fileName || "document",
        withBackground,
      );
      if (onClose) {
        onClose();
      }
      openSuccessNotification(
        "Your PDF is being prepared and will download automatically once ready. Feel free to continue your work in the meantime!",
        "Download in Progress",
      );
      setIsExporting(false);
    } catch (e: any) {
      setIsExporting(false);
      handleErrorMessage(e);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        border: "1px solid #e8eaed",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          padding: "12px 16px",
          borderBottom: "1px solid #ddd",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {fileName ? `${fileName} - HTML Editor` : "HTML Editor"}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" mr={2}>
          <CustomButton
            label={isExporting ? " Exporting" : " Export PDF"}
            onClick={(e: React.MouseEvent<HTMLElement>) =>
              setExportMenuAnchor(e.currentTarget)
            }
            type={strings.PRIMARY}
            icon={
              <Box mr={1} display={"flex"} alignItems={"center"}>
                {isExporting ? (
                  <CircularProgress size={16} sx={{ color: "black" }} />
                ) : (
                  <PictureAsPdf />
                )}
              </Box>
            }
            disabled={isExporting}
          />
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 0.5,
                borderRadius: "12px",
                minWidth: 210,
                boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                border: "1px solid rgba(0,0,0,0.08)",
              },
            }}
          >
            <MenuItem
              onClick={() => exportToPdf(true)}
              sx={{ py: 1.2, px: 2, gap: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: "auto" }}>
                <WallpaperOutlined fontSize="small" sx={{ color: "#9053F6" }} />
              </ListItemIcon>
              <ListItemText
                primary="With Background"
                secondary="Includes page background colours"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => exportToPdf(false)}
              sx={{ py: 1.2, px: 2, gap: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: "auto" }}>
                <ImageOutlined fontSize="small" sx={{ color: "#5E75FF" }} />
              </ListItemIcon>
              <ListItemText
                primary="Without Background"
                secondary="Clean white background only"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          padding: "8px 16px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleLocalImageUpload}
          style={{ display: "none" }}
        />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <IconButton
            onClick={undo}
            disabled={!canUndo}
            size="small"
            title="Undo"
          >
            <Undo />
          </IconButton>
          <IconButton
            onClick={redo}
            disabled={!canRedo}
            size="small"
            title="Redo"
          >
            <Redo />
          </IconButton>
          <Divider orientation="vertical" flexItem />

          {toolbarButtons.map((button, index) => (
            <IconButton
              key={index}
              onClick={() => execCommand(button.command, button.value)}
              size="small"
              title={button.title}
            >
              {button.icon}
            </IconButton>
          ))}
          <Divider orientation="vertical" flexItem />

          <IconButton
            onClick={() => setShowFontColorDialog(true)}
            size="small"
            title="Font Color"
          >
            <FormatColorText />
          </IconButton>

          <IconButton
            onClick={() => setShowFillColorDialog(true)}
            size="small"
            title="Fill Color"
          >
            <FormatColorFill />
          </IconButton>

          <IconButton
            onClick={() => setShowFontSizeDialog(true)}
            size="small"
            title="Font Size"
          >
            <FormatSize />
          </IconButton>

          <Divider orientation="vertical" flexItem />

          <IconButton
            onClick={() => fileInputRef.current?.click()}
            size="small"
            title="Upload Local Image"
          >
            <Image />
          </IconButton>

          <IconButton
            onClick={() => setShowImageDialog(true)}
            size="small"
            title="Insert Image URL"
          >
            <Link />
          </IconButton>
        </Stack>
      </Box>

      <Box
        ref={editorRef}
        contentEditable={true}
        onInput={updateContent}
        onBlur={updateContent}
        onFocus={handleEditorFocus}
        onKeyDown={(e) => {
          if (e.ctrlKey || e.metaKey) {
            switch (e.key) {
              case "b":
                e.preventDefault();
                execCommand("bold");
                break;
              case "i":
                e.preventDefault();
                execCommand("italic");
                break;
              case "u":
                e.preventDefault();
                execCommand("underline");
                break;
              case "z":
                if (e.shiftKey) {
                  e.preventDefault();
                  redo();
                } else {
                  e.preventDefault();
                  undo();
                }
                break;
            }
          }
        }}
        sx={{
          flex: 1,
          padding: "16px",
          overflowY: "auto",
          minHeight: "400px",
          border: "none",
          outline: "none",
          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          lineHeight: 1.6,
          "&:focus": {
            outline: "none",
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            margin: "8px 0",
          },
          "& table": {
            borderCollapse: "collapse",
            width: "100%",
            margin: "8px 0",
          },
          "& th, & td": {
            border: "1px solid #ddd",
            padding: "8px",
            textAlign: "left",
          },
          "& th": {
            backgroundColor: "#f2f2f2",
            fontWeight: "bold",
          },
          "& blockquote": {
            margin: "16px 0",
            padding: "12px 20px",
            borderLeft: "4px solid #ccc",
            backgroundColor: "#f9f9f9",
            fontStyle: "italic",
          },
          "& pre": {
            backgroundColor: "#f5f5f5",
            padding: "12px",
            borderRadius: "4px",
            overflow: "auto",
            fontFamily: "monospace",
          },
          "& ul, & ol": {
            paddingLeft: "20px",
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            margin: "16px 0 8px 0",
            fontWeight: "bold",
          },
          "& h1": { fontSize: "24px" },
          "& h2": { fontSize: "20px" },
          "& h3": { fontSize: "18px" },
          "& h4": { fontSize: "16px" },
          "& h5": { fontSize: "14px" },
          "& h6": { fontSize: "12px" },
        }}
      />

      <Modal
        open={showImageDialog}
        onClose={() => setShowImageDialog(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 450,
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            animation: "modalSlideIn 0.3s ease-out",
            "@keyframes modalSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) scale(0.9)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            mb={3}
            sx={{
              fontWeight: 600,
              color: "#1a1a1a",
              borderBottom: "2px solid #e0e0e0",
              paddingBottom: "12px",
            }}
          >
            Insert Image
          </Typography>
          <Stack spacing={3}>
            <TextField
              label="Image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              fullWidth
              placeholder="https://example.com/image.jpg"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <TextField
              label="Alt Text"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              fullWidth
              placeholder="Image description"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <CustomButton
                onClick={() => setShowImageDialog(false)}
                label="Cancel"
                customClasses={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              />
              <CustomButton
                onClick={handleImageInsert}
                disabled={!imageUrl.trim()}
                label="Insert"
                type={strings.PRIMARY}
                customClasses={{
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#999",
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 450,
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            animation: "modalSlideIn 0.3s ease-out",
            "@keyframes modalSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) scale(0.9)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            mb={3}
            sx={{
              fontWeight: 600,
              color: "#1a1a1a",
              borderBottom: "2px solid #e0e0e0",
              paddingBottom: "12px",
            }}
          >
            Insert Link
          </Typography>
          <Stack spacing={3}>
            <TextField
              label="URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              fullWidth
              placeholder="https://example.com"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <TextField
              label="Link Text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              fullWidth
              placeholder="Click here"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <Button
                onClick={() => setShowLinkDialog(false)}
                sx={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleLinkInsert}
                disabled={!linkUrl.trim() || !linkText.trim()}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#999",
                  },
                }}
              >
                Insert
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={showSizeDialog}
        onClose={() => setShowSizeDialog(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 450,
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            animation: "modalSlideIn 0.3s ease-out",
            "@keyframes modalSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) scale(0.9)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            mb={3}
            sx={{
              fontWeight: 600,
              color: "#1a1a1a",
              borderBottom: "2px solid #e0e0e0",
              paddingBottom: "12px",
            }}
          >
            Select Image Size
          </Typography>
          <Stack spacing={3}>
            <Typography
              variant="body1"
              color="text.secondary"
              mb={2}
              sx={{
                backgroundColor: "#f8f9fa",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              Choose the size for your uploaded image:
            </Typography>

            <Stack spacing={2}>
              <CustomButton
                label="Small (200px width)"
                onClick={() => handleImageSizeSelect("200px")}
                type={strings.PRIMARY}
                customClasses={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
              <CustomButton
                label="Medium (400px width)"
                onClick={() => handleImageSizeSelect("400px")}
                type={strings.PRIMARY}
                customClasses={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
              <CustomButton
                label="Large (600px width)"
                onClick={() => handleImageSizeSelect("600px")}
                type={strings.PRIMARY}
                customClasses={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
              <CustomButton
                label="Extra Large (800px width)"
                onClick={() => handleImageSizeSelect("800px")}
                type={strings.PRIMARY}
                customClasses={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
              <CustomButton
                label="Original Size"
                onClick={() => handleImageSizeSelect("auto")}
                type={strings.PRIMARY}
                customClasses={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <CustomButton
                onClick={() => setShowSizeDialog(false)}
                label="Cancel"
                customClasses={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={showFontColorDialog}
        onClose={() => setShowFontColorDialog(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 480,
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(20px)",
            animation: "modalSlideIn 0.3s ease-out",
            "@keyframes modalSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) scale(0.9)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            mb={3}
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: "20px",
              letterSpacing: "-0.5px",
              borderBottom: "2px solid #f0f0f0",
              paddingBottom: "16px",
            }}
          >
            Select Font Color
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={1}
                color="#5f6368"
              >
                Custom Color
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  backgroundColor: "#fafbfc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e8eaed",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={500} color="#5f6368">
                    Pick Color
                  </Typography>
                  <input
                    type="color"
                    value={fontColor}
                    onChange={(e) => setFontColor(e.target.value)}
                    style={{
                      width: "50px",
                      height: "50px",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      backgroundColor: fontColor,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="#5f6368">
                    Hex Value
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily:
                        "'Fira Code', 'Monaco', 'Consolas', monospace",
                      fontWeight: 600,
                      color: "#1a1a1a",
                      backgroundColor: "#f8f9fa",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: "1px solid #e8eaed",
                    }}
                  >
                    {fontColor.toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="#5f6368">
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: "1px solid #e8eaed",
                      backgroundColor: "#ffffff",
                      minWidth: "100px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: fontColor,
                        fontWeight: 500,
                      }}
                    >
                      Sample Text
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={1}
                color="#5f6368"
              >
                Quick Colors
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(8, 1fr)",
                  gap: 1,
                }}
              >
                {[
                  "#000000",
                  "#434343",
                  "#666666",
                  "#999999",
                  "#b7b7b7",
                  "#cccccc",
                  "#d9d9d9",
                  "#ffffff",
                  "#820014",
                  "#a8071a",
                  "#c41d7f",
                  "#eb2f96",
                  "#f759ab",
                  "#ffadd2",
                  "#ffd6e7",
                  "#fff0f6",
                  "#ad2102",
                  "#d4380d",
                  "#d46b08",
                  "#d48806",
                  "#d4b106",
                  "#fadb14",
                  "#fefce8",
                  "#fcffe6",
                  "#389e0d",
                  "#52c41a",
                  "#73d13d",
                  "#95de64",
                  "#b7eb8f",
                  "#d9f7be",
                  "#f6ffed",
                  "#f6ffed",
                  "#006d75",
                  "#08979c",
                  "#13c2c2",
                  "#36cfc9",
                  "#5cdbd3",
                  "#87e8de",
                  "#b5f5ec",
                  "#e6fffb",
                  "#003eb3",
                  "#0958d9",
                  "#1677ff",
                  "#4096ff",
                  "#69b1ff",
                  "#91caff",
                  "#bae0ff",
                  "#e6f4ff",
                  "#061178",
                  "#10239e",
                  "#1d39c4",
                  "#2f54eb",
                  "#597ef7",
                  "#85a5ff",
                  "#adc6ff",
                  "#d6e4ff",
                  "#22075e",
                  "#391085",
                  "#531dab",
                  "#722ed1",
                  "#9254de",
                  "#b37feb",
                  "#d3adf7",
                  "#f9f0ff",
                ].map((color, index) => (
                  <Box
                    key={index}
                    onClick={() => setFontColor(color)}
                    sx={{
                      width: "35px",
                      height: "35px",
                      backgroundColor: color,
                      borderRadius: "6px",
                      cursor: "pointer",
                      border:
                        color === "#ffffff" ? "1px solid #e8eaed" : "none",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={1}>
              <CustomButton
                onClick={() => setShowFontColorDialog(false)}
                label="Cancel"
                customClasses={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              />
              <CustomButton
                onClick={handleFontColorApply}
                label="Apply Color"
                type={strings.PRIMARY}
                customClasses={{
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={showFillColorDialog}
        onClose={() => setShowFillColorDialog(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 480,
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(20px)",
            animation: "modalSlideIn 0.3s ease-out",
            "@keyframes modalSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) scale(0.9)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            mb={3}
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: "20px",
              letterSpacing: "-0.5px",
              borderBottom: "2px solid #f0f0f0",
              paddingBottom: "16px",
            }}
          >
            Select Fill Color
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={1}
                color="#5f6368"
              >
                Custom Color
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  backgroundColor: "#fafbfc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e8eaed",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={500} color="#5f6368">
                    Pick Color
                  </Typography>
                  <input
                    type="color"
                    value={fillColor}
                    onChange={(e) => setFillColor(e.target.value)}
                    style={{
                      width: "50px",
                      height: "50px",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      backgroundColor: fillColor,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="#5f6368">
                    Hex Value
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily:
                        "'Fira Code', 'Monaco', 'Consolas', monospace",
                      fontWeight: 600,
                      color: "#1a1a1a",
                      backgroundColor: "#f8f9fa",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: "1px solid #e8eaed",
                    }}
                  >
                    {fillColor.toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="#5f6368">
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: "1px solid #e8eaed",
                      backgroundColor: fillColor,
                      minWidth: "100px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: fillColor === "#ffffff" ? "#1a1a1a" : "#ffffff",
                        fontWeight: 500,
                      }}
                    >
                      Sample Text
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={1}
                color="#5f6368"
              >
                Quick Colors
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(8, 1fr)",
                  gap: 1,
                }}
              >
                {[
                  "#ffffff",
                  "#fafafa",
                  "#f5f5f5",
                  "#f0f0f0",
                  "#e6e6e6",
                  "#d9d9d9",
                  "#bfbfbf",
                  "#8c8c8c",
                  "#fff1f0",
                  "#ffccc7",
                  "#ffa39e",
                  "#ff7875",
                  "#ff4d4f",
                  "#f5222d",
                  "#cf1322",
                  "#820014",
                  "#fff7e6",
                  "#ffe7ba",
                  "#ffd591",
                  "#ffc53d",
                  "#faad14",
                  "#d48806",
                  "#ad6800",
                  "#873800",
                  "#f6ffed",
                  "#d9f7be",
                  "#b7eb8f",
                  "#95de64",
                  "#73d13d",
                  "#52c41a",
                  "#389e0d",
                  "#237804",
                  "#e6fffb",
                  "#b5f5ec",
                  "#87e8de",
                  "#5cdbd3",
                  "#36cfc9",
                  "#13c2c2",
                  "#08979c",
                  "#006d75",
                  "#f0f5ff",
                  "#d6e4ff",
                  "#adc6ff",
                  "#85a5ff",
                  "#597ef7",
                  "#2f54eb",
                  "#1d39c4",
                  "#10239e",
                  "#f9f0ff",
                  "#efdbff",
                  "#d3adf7",
                  "#b37feb",
                  "#9254de",
                  "#722ed1",
                  "#531dab",
                  "#391085",
                  "#fcffe6",
                  "#f4ffb8",
                  "#eaff8f",
                  "#d3f261",
                  "#bae637",
                  "#a0d911",
                  "#7cb305",
                  "#389e0d",
                ].map((color, index) => (
                  <Box
                    key={index}
                    onClick={() => setFillColor(color)}
                    sx={{
                      width: "35px",
                      height: "35px",
                      backgroundColor: color,
                      borderRadius: "6px",
                      cursor: "pointer",
                      border:
                        color === "#ffffff" ? "1px solid #e8eaed" : "none",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={1}>
              <CustomButton
                onClick={() => setShowFillColorDialog(false)}
                label="Cancel"
                customClasses={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              />
              <CustomButton
                onClick={handleFillColorApply}
                label="Apply Fill"
                type={strings.PRIMARY}
                customClasses={{
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={showFontSizeDialog}
        onClose={() => setShowFontSizeDialog(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 480,
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(20px)",
            animation: "modalSlideIn 0.3s ease-out",
            "@keyframes modalSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) scale(0.9)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            mb={3}
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: "20px",
              letterSpacing: "-0.5px",
              borderBottom: "2px solid #f0f0f0",
              paddingBottom: "16px",
            }}
          >
            Select Font Size
          </Typography>

          <Stack spacing={4}>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={2}
                color="#5f6368"
              >
                Custom Size
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#fafbfc",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #e8eaed",
                }}
              >
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500, color: "#5f6368" }}>
                    Font Size
                  </InputLabel>
                  <Select
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    label="Font Size"
                    sx={{
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e8eaed",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2",
                        borderWidth: "2px",
                      },
                    }}
                  >
                    <MenuItem value="8px" sx={{ fontSize: "8px" }}>
                      8px - Very Small
                    </MenuItem>
                    <MenuItem value="10px" sx={{ fontSize: "10px" }}>
                      10px - Small
                    </MenuItem>
                    <MenuItem value="12px" sx={{ fontSize: "12px" }}>
                      12px - Regular
                    </MenuItem>
                    <MenuItem value="14px" sx={{ fontSize: "14px" }}>
                      14px - Medium
                    </MenuItem>
                    <MenuItem value="16px" sx={{ fontSize: "16px" }}>
                      16px - Large
                    </MenuItem>
                    <MenuItem value="18px" sx={{ fontSize: "18px" }}>
                      18px - Extra Large
                    </MenuItem>
                    <MenuItem value="20px" sx={{ fontSize: "20px" }}>
                      20px - Huge
                    </MenuItem>
                    <MenuItem value="24px" sx={{ fontSize: "24px" }}>
                      24px - Title
                    </MenuItem>
                    <MenuItem value="28px" sx={{ fontSize: "28px" }}>
                      28px - Heading
                    </MenuItem>
                    <MenuItem value="32px" sx={{ fontSize: "32px" }}>
                      32px - Large Heading
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={2}
                color="#5f6368"
              >
                Preview
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#fafbfc",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #e8eaed",
                  minHeight: "80px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: fontSize,
                    fontWeight: 500,
                    color: "#1a1a1a",
                    lineHeight: 1.4,
                  }}
                >
                  This is how your text will look with {fontSize} font size.
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <CustomButton
                onClick={() => setShowFontSizeDialog(false)}
                label="Cancel"
                customClasses={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              />
              <CustomButton
                onClick={handleFontSizeApply}
                label="Apply Size"
                type={strings.PRIMARY}
                customClasses={{
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default HtmlEditor;
